import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { Eyebrow, HighlightedText } from "blog/components/ui.js";
import Avatar from "blog/components/Avatar.js";
import { ReactComponent as SvgDecoratorBlob7 } from "images/svg-decorator-blob-7.svg";
import { membres as membresParDefaut } from "home/data/equipe.js";

/**
 * Section « L’équipe » de la page d’accueil.
 *
 * Présente la personne derrière le journal (crédibilité éditoriale) et son ouverture aux contributions.
 * Même recette visuelle que components/cards/ProfileThreeColGrid.js (portrait rond,
 * cadré au centre), rendue localement pour corriger ce que le composant partagé ne
 * permet pas : portraits en <img> avec alt et dimensions, liens sociaux nommés à zone
 * tactile élargie, surtitre en <span> avant le <h2>, uniquement des liens réels
 * (données dans src/home/data/equipe.js).
 * Pas de bouton ici : la prise de contact est assurée par la section suivante.
 */

const Wrap = tw(Container)``;
const HeadingContainer = tw.div`flex flex-col items-center text-center`;
const Subheading = tw(Eyebrow)`block mb-3`;
const Heading = tw(SectionHeading)``;
/* secondary-300 : même bleu nuit que la DA, mais contraste AA sur fond blanc. */
const Description = tw(SectionDescription)`mx-auto text-center text-secondary-300`;

const Cards = tw.ul`flex flex-wrap flex-row justify-center sm:max-w-2xl lg:max-w-5xl mx-auto list-none`;
const Card = tw.li`mt-24 w-full sm:w-1/2 lg:w-1/3 flex flex-col items-center`;
/* Les deux portraits (427×412 et 504×495) sont recadrés à la même échelle dans un rond de 256 px. */
// Photo si elle existe, sinon monogramme (aucune photo publiée aujourd'hui).
const Portrait = tw(Avatar)``;
const CardContent = tw.div`flex flex-col items-center mt-6 text-center`;
const Position = tw.span`uppercase font-bold tracking-widest text-xs text-primary-500`;
const Name = tw.h3`mt-1 text-xl font-medium text-gray-900`;
const Links = tw.div`mt-6 flex`;
/* Zone cliquable de 40 × 40 px (p-2 compensé par -m-2) pour un espacement visuel inchangé. */
const SocialLink = styled.a`
  ${tw`p-2 -m-2 mr-6 last:mr-0 inline-flex text-gray-600 hocus:text-primary-500 transition duration-300 rounded focus:outline-none focus:shadow-outline`}
  svg {
    ${tw`fill-current w-6 h-6`}
  }
`;

/* Décor : blob violet en haut à GAUCHE, côté colonne texte de « Se former » (son motif de
   points et son illustration occupent déjà le bord droit de la jonction). */
const DecoratorBlob = tw(
  SvgDecoratorBlob7
)`pointer-events-none -z-10 absolute left-0 top-0 h-64 w-64 opacity-15 transform -translate-x-1/2 -translate-y-16 text-primary-500`;

const DEFAULT_SUBHEADING = "Derrière le journal";
const DEFAULT_HEADING = (
  <>
    Un journal indépendant, <HighlightedText>à but non lucratif.</HighlightedText>
  </>
);
/* Statut (formation en cours ou achevée) à confirmer avec Jimmy et à aligner avec /about. */
const DEFAULT_DESCRIPTION =
  "IA Besançon est écrit par passion, sur du temps libre, pour mettre à découvert les technologies émergentes de la région et transmettre cette curiosité. Le journal est ouvert aux contributions : un sujet, un témoignage, une visite d’atelier ou de laboratoire.";

export default function Equipe({
  id = "equipe",
  subheading = DEFAULT_SUBHEADING,
  heading = DEFAULT_HEADING,
  description = DEFAULT_DESCRIPTION,
  membres = membresParDefaut,
}) {
  return (
    <Wrap id={id}>
      <ContentWithPaddingXl>
        <HeadingContainer>
          {subheading && <Subheading>{subheading}</Subheading>}
          <Heading>{heading}</Heading>
          {description && <Description>{description}</Description>}
        </HeadingContainer>
        <Cards role="list">
          {membres.map(membre => (
            <Card key={membre.name}>
              <Portrait name={membre.name} image={membre.imageSrc} size={256} />
              <CardContent>
                <Position>{membre.position}</Position>
                <Name>{membre.name}</Name>
                {membre.links && membre.links.length > 0 && (
                  <Links>
                    {membre.links.map(link => (
                      <SocialLink
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${link.label} de ${membre.name} (s’ouvre dans un nouvel onglet)`}
                      >
                        <link.icon aria-hidden="true" focusable="false" />
                      </SocialLink>
                    ))}
                  </Links>
                )}
              </CardContent>
            </Card>
          ))}
        </Cards>
      </ContentWithPaddingXl>
      <DecoratorBlob aria-hidden="true" focusable="false" />
    </Wrap>
  );
}
